import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { trackCompletedEditorChecklistItem } from '../tracking';
import { getCompletedStepsAmount } from '../utils';

export const EDITOR_CHECKLIST_STORAGE_KEY = 'editor_checklist_state';

export const ChecklistItemKey = {
  ADD_SLIDE: 'add-slide',
  EDIT_CONTENT: 'edit-content',
  PICK_THEME: 'pick-theme',
  TRY_MENTI: 'try-menti',
  WRITE_QUESTION: 'write-question',
} as const;

// Use the object values as a type
export type ChecklistItemKey =
  (typeof ChecklistItemKey)[keyof typeof ChecklistItemKey];

export interface ChecklistItemState {
  key: ChecklistItemKey;
  text: string;
  completed: boolean;
  disabled: boolean;
  active: boolean;
}

export type UpdateItemFunction = (
  key: ChecklistItemKey,
  updates: Partial<ChecklistItemState>,
) => void;

export type IsItemActiveFunction = (
  key: ChecklistItemKey,
) => boolean | undefined;

interface ChecklistState {
  isOpen: boolean;
  items: ChecklistItemState[];
  toggleOpen: (value: boolean) => void;
  resetChecklist: () => void;
  updateItem: UpdateItemFunction;
  isItemActive: IsItemActiveFunction;
}

const defaultItems: ChecklistItemState[] = [
  {
    key: 'add-slide',
    text: 'Add a slide',
    completed: false,
    active: false,
    disabled: false,
  },
  {
    key: 'write-question',
    text: 'Write your question',
    completed: false,
    active: false,
    disabled: false,
  },
  {
    key: 'edit-content',
    text: 'Edit slide content',
    completed: false,
    active: false,
    disabled: false,
  },
  {
    key: 'pick-theme',
    text: 'Pick a color theme',
    completed: false,
    active: false,
    disabled: false,
  },
  {
    key: 'try-menti',
    text: 'Try out your Menti',
    completed: false,
    active: false,
    disabled: false,
  },
];

export const resetItemsActiveState = (
  items: ChecklistItemState[],
): ChecklistItemState[] => items.map((item) => ({ ...item, active: false }));

export const createUpdatedChecklistItemsWithTracking = (
  items: ChecklistItemState[],
  key: ChecklistItemKey,
  updates: Partial<ChecklistItemState>,
): ChecklistItemState[] => {
  const itemIndex = items.findIndex((item) => item.key === key);
  const wasTracked = items[itemIndex]?.completed === true;

  if (updates.completed === true && !wasTracked) {
    // In order to have correct progress tracking, we need to increment the completed steps count before the actual update
    trackCompletedEditorChecklistItem(key, getCompletedStepsAmount(items) + 1);
  }

  return items.map((item, index) => {
    if (index === itemIndex) {
      return { ...item, ...updates };
    }
    // Reset active state for all other items if the current item is being activated
    if (updates.active === true) {
      return { ...item, active: false };
    }
    return item;
  });
};

export const isChecklistItemActive = (
  items: ChecklistItemState[],
  key: ChecklistItemKey,
): boolean | undefined => {
  const item = items.find((item) => item.key === key);
  return item ? item.active : false;
};

export const useChecklistStore = create<ChecklistState>()(
  persist(
    (set, get) => ({
      isOpen: true,
      items: defaultItems,

      toggleOpen: (isOpen: boolean) => {
        set(() => ({ isOpen }));
        if (!isOpen) {
          const updatedItems = resetItemsActiveState(get().items);
          set({ items: updatedItems });
        }
      },

      updateItem: (
        key: ChecklistItemKey,
        updates: Partial<ChecklistItemState>,
      ) => {
        const { items } = get();
        const newItems = createUpdatedChecklistItemsWithTracking(
          items,
          key,
          updates,
        );
        set({ items: newItems });
      },

      resetChecklist: () => {
        set({ items: defaultItems });
      },

      isItemActive: (key: ChecklistItemKey) => {
        const { items } = get();
        return isChecklistItemActive(items, key);
      },
    }),
    {
      name: EDITOR_CHECKLIST_STORAGE_KEY,
      partialize: (state) => ({
        isOpen: state.isOpen,
        items: state.items,
      }),
    },
  ),
);
